import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { theme } from '../styles/GlobalStyles';

const AboutSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background: ${theme.colors.surface};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing.xxl};
  color: ${theme.colors.text};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.xxl};
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const AboutText = styled(motion.div)`
  color: ${theme.colors.textSecondary};
  line-height: 1.8;
  font-size: 1.1rem;
`;

const SkillsContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const SkillCategory = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: ${theme.spacing.lg};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SkillCategoryTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.2rem;
  margin-bottom: ${theme.spacing.md};
  font-weight: 600;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const SkillTag = styled(motion.span)`
  background: rgba(37, 99, 235, 0.2);
  color: ${theme.colors.primary};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(37, 99, 235, 0.3);
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled.div`
  text-align: center;
  padding: ${theme.spacing.lg};
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const StatLabel = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SectionDivider = styled.div`
  width: 60px;
  height: 4px;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: 2px;
  margin: ${theme.spacing.lg} 0;
`;

const AboutSubsection = styled(motion.div)`
  margin-bottom: ${theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubsectionTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const SubsectionIcon = styled.span`
  font-size: 1.5rem;
`;

const SubsectionText = styled.p`
  color: ${theme.colors.textSecondary};
  line-height: 1.8;
  font-size: 1.1rem;
  margin: 0;
`;

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const skills = {
    'AWS & Cloud Technologies': ['AWS Solutions Architect Pro', 'AWS Developer Associate', 'CDK', 'Lambda', 'S3', 'DynamoDB'],
    'Life Sciences & Biotech': ['Genomics Data Science', 'Scientific Data Management', 'Bioinformatics', 'Data Lakes', 'Research Workflows'],
    'Programming & Technical': ['Python', 'C/C++', 'LabVIEW', 'MySQL', 'PHP', 'Ruby', 'Industrial Automation'],
    'Business & Leadership': ['Go-to-Market Strategy', 'Sales Leadership', 'Partnership Development', 'Revenue Growth', 'MBA Entrepreneurship'],
  };

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '$2.4M', label: 'ARR Achieved' },
    { number: '3x', label: 'President\'s Club' },
    { number: '2018', label: 'MBA Top Graduate' },
  ];

  return (
    <AboutSection id="about" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          About Me
        </SectionTitle>

        <ContentGrid>
          <AboutText
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <AboutSubsection
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <SubsectionTitle>
                <SubsectionIcon>🎓</SubsectionIcon>
                Education & Recognition
              </SubsectionTitle>
              <SubsectionText>
                I hold an MBA from Babson College, consistently ranked as one of the nation's top three 
                business schools for entrepreneurship. I was honored as the 2018 Winner of the Roger 
                Babson Award and the Babson College Alumni Association award, recognizing the top 
                overall class graduate in service, business acumen and contributions to the university.
              </SubsectionText>
            </AboutSubsection>

            <SectionDivider />

            <AboutSubsection
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <SubsectionTitle>
                <SubsectionIcon>🚀</SubsectionIcon>
                Current Leadership Role
              </SubsectionTitle>
              <SubsectionText>
                As Director of Sales and Marketing at Quilt, I lead go-to-market strategy for a 
                metadata-driven Scientific Data Management Platform serving life sciences and biotech 
                organizations. I've successfully increased revenue from $1.2M to $2.4M ARR while securing 
                partnerships with industry leaders including Biogen, Amgen, Flagship Pioneering, and 
                Vertex Pharmaceuticals.
              </SubsectionText>
            </AboutSubsection>

            <SectionDivider />

            <AboutSubsection
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <SubsectionTitle>
                <SubsectionIcon>⚡</SubsectionIcon>
                Technical Expertise & Vision
              </SubsectionTitle>
              <SubsectionText>
                My unique combination of technical depth (AWS Solutions Architect Professional, Genomics 
                Data Science certified) and business leadership experience spans across multiple successful 
                ventures, from co-founding startups to leading enterprise sales teams. I'm passionate about 
                bridging the gap between cutting-edge technology and real-world business impact.
              </SubsectionText>
            </AboutSubsection>
          </AboutText>

          <SkillsContainer
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {Object.entries(skills).map(([category, skillList], index) => (
              <SkillCategory
                key={category}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
              >
                <SkillCategoryTitle>{category}</SkillCategoryTitle>
                <SkillsList>
                  {skillList.map((skill, skillIndex) => (
                    <SkillTag
                      key={skill}
                      whileHover={{ scale: 1.05, y: -2 }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.4, delay: 0.8 + index * 0.1 + skillIndex * 0.05 }}
                    >
                      {skill}
                    </SkillTag>
                  ))}
                </SkillsList>
              </SkillCategory>
            ))}
          </SkillsContainer>
        </ContentGrid>

        <StatsContainer
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          {stats.map((stat, index) => (
            <StatItem
              key={stat.label}
              as={motion.div}
              whileHover={{ scale: 1.05, y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
            >
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatItem>
          ))}
        </StatsContainer>
      </Container>
    </AboutSection>
  );
};

export default About;
